import React, { useEffect, useState } from 'react';
import { Box, Typography, Divider, CircularProgress } from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import { format } from 'date-fns';
import esLocale from 'date-fns/locale/es'; // Para mostrar fechas en español
import { motion } from 'framer-motion';
import axiosInstance from '../../../services/axiosInstance';
import {
  AccessTime,
  Cancel,
  CardGiftcard,
  Payment,
  PersonAdd,
  PersonRemove,
  Edit,
  MoneyOff,
  ThumbUp,
} from '@mui/icons-material';

const BookingHistory = ({ bookingId }) => {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await axiosInstance.get(
          `/users/booking/${bookingId}/history`
        );
        if (response.success) {
          setHistory(response.history);
        } else {
          console.error('No se pudo cargar el historial de la reserva.');
        }
      } catch (error) {
        console.error('Error al cargar historial:', error);
      } finally {
        setLoadingHistory(false);
      }
    };

    fetchHistory();
  }, [bookingId]);

  // Función para asignar un ícono basado en la acción
  const getIconByAction = action => {
    if (action.includes('Pago')) return <Payment />;
    if (action.includes('Cancelado')) return <Cancel />;
    if (action.includes('invitado')) return <MoneyOff />;
    if (action.includes('Invitación')) return <CardGiftcard />;
    if (action.includes('añadido')) return <PersonAdd />;
    if (action.includes('eliminado')) return <PersonRemove />;
    if (action.includes('Confirmado')) return <ThumbUp />;
    if (action.includes('Cambio de horario')) return <AccessTime />;
    return <Edit />;
  };

  // Función para asignar color basado en la acción
  const getColorByAction = action => {
    if (action.includes('Pago')) return 'success';
    if (action.includes('Cancelado')) return 'error';
    if (action.includes('Invitación') || action.includes('invitado'))
      return 'warning';
    if (action.includes('añadido')) return 'info';
    if (action.includes('eliminado')) return 'secondary';
    if (action.includes('Confirmado')) return 'success';
    if (action.includes('Cambio de horario')) return 'primary';
    return 'grey';
  };

  // Agrupar eventos por día
  const groupByDate = history.reduce((acc, entry) => {
    const date = format(new Date(entry.timestamp), 'dd/MM/yyyy', {
      locale: esLocale,
    });
    if (!acc[date]) acc[date] = [];
    acc[date].push(entry);
    return acc;
  }, {});

  return (
    <Box mt={4}>
      <Typography variant="h5" gutterBottom textAlign="center">
        Historial de la Reserva
      </Typography>
      <Divider sx={{ mb: 3 }} />
      {loadingHistory ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <CircularProgress />
        </Box>
      ) : Object.keys(groupByDate).length > 0 ? (
        <Timeline position="alternate">
          {Object.entries(groupByDate).map(([date, events], dateIndex) => (
            <motion.div
              key={dateIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: dateIndex * 0.2 }}
            >
              {/* Encabezado por día */}
              <Typography
                variant="h6"
                color="primary"
                sx={{ mt: 2, mb: 2, textAlign: 'center' }}
              >
                {date}
              </Typography>
              {events.map((entry, index) => (
                <TimelineItem key={index}>
                  <TimelineSeparator>
                    <TimelineDot color={getColorByAction(entry.action)}>
                      {getIconByAction(entry.action)}
                    </TimelineDot>
                    {index !== events.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {entry.action}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {format(
                        new Date(entry.timestamp),
                        "dd 'de' MMMM yyyy, HH:mm:ss",
                        { locale: esLocale }
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      Realizado por:{' '}
                      {entry.performedBy
                        ? `${entry.performedBy.name || ''} ${entry.performedBy.surname || ''}`.trim()
                        : 'Usuario desconocido'}
                    </Typography>
                    {entry.details && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {entry.details}
                      </Typography>
                    )}
                  </TimelineContent>
                </TimelineItem>
              ))}
            </motion.div>
          ))}
        </Timeline>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No hay historial registrado para esta reserva.
        </Typography>
      )}
    </Box>
  );
};

export default BookingHistory;
